import { Directive, Input, OnDestroy, OnInit, Optional, TemplateRef, ViewContainerRef, ElementRef, Renderer2, HostListener } from '@angular/core';
import { Subscription } from 'rxjs';
import { CommonDataStorage } from '../common/commonDataStore';

@Directive({
  selector: '[appHasPermission]'
})
export class HasPermissionDirective implements OnInit, OnDestroy {
  private sub = new Subscription();
  private hasView = false;
  private allowed = false;
  @Input('appHasPermission') permissionInput?: string | string[];
  @Input('appHasPermissionElse') elseTpl?: TemplateRef<any>;
  constructor(
    @Optional() private tpl: TemplateRef<any> | null,
    private vcr: ViewContainerRef,
    private commonDataStorage: CommonDataStorage,
    @Optional() private el?: ElementRef,
    @Optional() private renderer?: Renderer2
  ) { }

  ngOnInit() {
    this.sub = this.commonDataStorage.getPermissions().subscribe((perms: any) => {
      const userAccess = perms?.Data?.user_access ?? perms ?? [];
      this.allowed = this.checkAllowed(userAccess, this.permissionInput);
      if (this.tpl) {
        this.vcr.clear();
        this.hasView = false;
        if (this.allowed) {
          this.vcr.createEmbeddedView(this.tpl);
          this.hasView = true;
        } else if (this.elseTpl) {
          this.vcr.createEmbeddedView(this.elseTpl);
        }
      } else {
        if (this.el && this.renderer) {
          if (!this.allowed) {
            this.renderer.addClass(this.el.nativeElement, 'permission-denied');
            this.renderer.setStyle(this.el.nativeElement, 'cursor', 'not-allowed');
            this.renderer.setStyle(this.el.nativeElement, 'opacity', '0.45');
            this.renderer.setStyle(this.el.nativeElement, 'pointer-events', 'none');
            this.renderer.setAttribute(this.el.nativeElement, 'tabindex', '-1');
            this.renderer.setAttribute(this.el.nativeElement, 'aria-disabled', 'true');
          } else {
            this.renderer.removeClass(this.el.nativeElement, 'permission-denied');
            this.renderer.removeStyle(this.el.nativeElement, 'cursor');
            this.renderer.removeStyle(this.el.nativeElement, 'opacity');
            this.renderer.removeStyle(this.el.nativeElement, 'pointer-events');
            this.renderer.removeAttribute(this.el.nativeElement, 'tabindex');
            this.renderer.removeAttribute(this.el.nativeElement, 'aria-disabled');
          }
        }
      }
    });
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }

  @HostListener('click', ['$event'])
  onHostClick(ev: Event) {
    if (!this.tpl && !this.allowed) {
      ev.preventDefault();
      ev.stopImmediatePropagation();
    }
  }

  private checkAllowed(userAccess: any[], input?: string | string[]): boolean {
    if (!input) return false;
    if (Array.isArray(input)) {
      for (const raw of input) {
        const [code, actionRaw] = ('' + raw).split(':').map(s => s.trim());
        const action = (actionRaw || 'view').toLowerCase();

        const acc = (userAccess || []).find(u => u.functionality__code === code);
        if (!acc) continue;

        if (action === 'view' && acc.can_view) return true;
        if ((action === 'create' || action === 'update' || action === 'create_update') && acc.can_create_update) return true;
        if (action === 'delete' && acc.can_delete) return true;
      }
      return false;
    }
    const [funcRaw, actionsRaw] = input.split(':').map(s => s.trim());
    const functionality = funcRaw;
    const actions = (actionsRaw || 'view').split(',').map(a => a.trim().toLowerCase());
    let acc = (userAccess || []).find((u: any) =>
      u.functionality__function_name && typeof u.functionality__function_name === 'string' &&
      u.functionality__function_name.toLowerCase() === functionality.toLowerCase()
    );
    if (!acc) acc = (userAccess || []).find((u: any) => u.functionality__code === functionality);
    if (!acc) return false;

    for (const action of actions) {
      if (action === 'view' && !acc.can_view) return false;
      if ((action === 'create' || action === 'update' || action === 'create_update') && !acc.can_create_update) return false;
      if (action === 'delete' && !acc.can_delete) return false;
    }
    return true;
  }
}
